<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import { computed, ref } from "vue";
import { PageProps, AlertTypeEnum } from "@/Types/index.d";
import {BlockResource, BlockTypeEnum, TenantThemeEnum} from "@/Types/generated.d";
import Error from "@/Components/Frontend/Form/Error.vue";
import Label from "@/Components/Frontend/Form/Label.vue";
import Button from "@/Components/Frontend/Elements/Button.vue";
import Input from "@/Components/Frontend/Form/Input.vue";
import Alert from "@/Components/Backend/Ui/Alert.vue";
import axios from "axios";
import { useSanitize } from '@/Composables/Shared/useSanitize';

// --- Props
// --- Data
const processing = ref<boolean>(false);
const errors = ref<Record<string, string[]>>({});
const success = ref<string | null>(null);
const email = ref<string | null>(null);
const sanitize = useSanitize();
// --- Computed
const initialPageProps = computed(() => usePage<PageProps>().props);
const block = computed<BlockResource | undefined>(() =>
    initialPageProps.value.blocks?.find((block) => {
        return block.type === BlockTypeEnum.NEWSLETTER;
    }),
);
// --- Methods
async function submit() {
    processing.value = true;
    success.value = null;
    errors.value = {};

    try {
        const response = await axios.post(route("frontend.newsletter"), {
            email: email.value,
        });

        success.value = response.data.message;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response && error.response.status === 422) {
            errors.value = error.response.data.errors;
        } else {
            console.error(error);
        }
    } finally {
        email.value = null;
        processing.value = false;
    }
}
// --- Watch
// --- Provide
</script>
<template>
    <div class="newsletter-block" v-if="initialPageProps.theme === TenantThemeEnum.IXOCOLLECTIONS">
        <div class="container">
            <Alert
                v-if="success"
                :type="AlertTypeEnum.SUCCESS"
                :message="success"
                :closeable="true"
                @close="success = null"
            />

            <p class="newsletter-block__title">{{ block?.title }}</p>
            <div class="newsletter-block__text prose max-w-none" v-html="sanitize.html(block?.content)"></div>

            <form class="flex items-center justify-center gap-5" @submit.prevent="submit">
                <div class="flex flex-col items-center justify-center">
                    <Label for="email" :value="$t('Email')" class="sr-only" />
                    <Input
                        id="email"
                        type="email"
                        v-model="email"
                        :has-error="'email' in errors"
                        placeholder="email@address.com"
                        class="flex-1 border-[#E1E1E1] text-center text-fsm lg:w-[558px]"
                        required
                        autocomplete="email"
                    />
                    <Error :message="errors.email?.[0]" />
                </div>

                <div class="flex justify-center">
                    <Button
                        class="!px-7 uppercase"
                        size="inline"
                        :processing="processing"
                        bg-color="black"
                        text-color="white"
                    >
                        OK
                    </Button>
                </div>
            </form>
        </div>
    </div>
</template>
<style lang="postcss" scoped>
.newsletter-block {
    @apply bg-[#FCFCFC] py-28;
}
.newsletter-block__title {
    @apply mb-7 text-center leading-tight text-fxl lg:text-f2xl font-[600] text-[#353535];
}
.newsletter-block__text {
    @apply mb-7 text-center text-fsm font-light text-[#353535];
}
</style>
